import api from './api';
import { esCreditoCobranzaCliente, esCreditoPagoProveedor, esDevolucionCobranzaCliente, esNotaCreditoProveedor } from '../constants/tiposComprobante';

export interface CreditoConSaldoPendiente {
  nroFormulario: number;
  nroTipoFormulario?: string;
  letra?: string;
  nroSuc: number;
  nroComprobante?: number;
  fechaEmision?: string;
  importeTotal?: number;
  totalAplicado: number;
  saldoPendiente: number;
}

export interface DebitoConSaldoPendiente {
  nroFormulario: number;
  nroTipoFormulario?: string;
  letra?: string;
  nroSuc: number;
  nroComprobante?: number;
  fechaEmision?: string;
  fechaVencim?: string;
  importeTotal?: number;
  importeCancelado: number;
  montoPendiente: number;
}

export interface AplicacionRecibo {
  nroIdformPagoCobro: number;
  nroIdformulario: number;
  nroTipoFormulario: string;
  letra: string;
  nroSuc?: number;
  nroComprobante: number;
  fechaEmision: string;
  fechaVencim: string;
  importeTotal?: number;
  importeCanceladoAnt?: number;
  fecha?: string;
  nroMovCaja: number;
  montoCancelado?: number;
}

export interface DocumentoAplicar {
  nroIdformulario: number;
  montoCancelado: number;
}

/** Crédito que imputó en un débito de cliente (vista desde factura / ND / devolución) */
export interface AplicacionCreditoEnDebito {
  nroIdformPagoCobro: number;
  nroTipoFormularioCredito: string;
  letra: string;
  nroSuc: number;
  nroComprobante: number;
  nroIdformularioDebito: number;
  fechaEmisionDebito: string;
  nroMovCaja: number;
  montoCancelado?: number;
  fechaAplicacion?: string;
}

export interface DesaplicarRequest {
  nroIdformulario: number;
  fechaEmision: string;
  nroMovCaja: number;
}

const mapCredito = (raw: Record<string, unknown>): CreditoConSaldoPendiente => ({
  nroFormulario: Number(raw.nroFormulario ?? raw.NroFormulario ?? 0),
  nroTipoFormulario: String(raw.nroTipoFormulario ?? raw.NroTipoFormulario ?? '').trim() || undefined,
  letra: (raw.letra ?? raw.Letra) as string | undefined,
  nroSuc: Number(raw.nroSuc ?? raw.NroSuc ?? 0),
  nroComprobante: Number(raw.nroComprobante ?? raw.NroComprobante ?? 0) || undefined,
  fechaEmision: (raw.fechaEmision ?? raw.FechaEmision) as string | undefined,
  importeTotal: Number(raw.importeTotal ?? raw.ImporteTotal ?? 0) || undefined,
  totalAplicado: Number(raw.totalAplicado ?? raw.TotalAplicado ?? 0),
  saldoPendiente: Number(raw.saldoPendiente ?? raw.SaldoPendiente ?? 0),
});

const mapDebito = (raw: Record<string, unknown>): DebitoConSaldoPendiente => ({
  nroFormulario: Number(raw.nroFormulario ?? raw.NroFormulario ?? 0),
  nroTipoFormulario: String(raw.nroTipoFormulario ?? raw.NroTipoFormulario ?? '').trim() || undefined,
  letra: (raw.letra ?? raw.Letra) as string | undefined,
  nroSuc: Number(raw.nroSuc ?? raw.NroSuc ?? 0),
  nroComprobante: Number(raw.nroComprobante ?? raw.NroComprobante ?? 0) || undefined,
  fechaEmision: (raw.fechaEmision ?? raw.FechaEmision) as string | undefined,
  fechaVencim: (raw.fechaVencim ?? raw.FechaVencim) as string | undefined,
  importeTotal: Number(raw.importeTotal ?? raw.ImporteTotal ?? 0) || undefined,
  importeCancelado: Number(raw.importeCancelado ?? raw.ImporteCancelado ?? 0),
  montoPendiente: Number(raw.montoPendiente ?? raw.MontoPendiente ?? 0),
});

const mapAplicacionCreditoEnDebito = (raw: Record<string, unknown>): AplicacionCreditoEnDebito => ({
  nroIdformPagoCobro: Number(raw.nroIdformPagoCobro ?? raw.NroIdformPagoCobro ?? 0),
  nroTipoFormularioCredito: String(raw.nroTipoFormularioCredito ?? raw.NroTipoFormularioCredito ?? ''),
  letra: String(raw.letra ?? raw.Letra ?? ''),
  nroSuc: Number(raw.nroSuc ?? raw.NroSuc ?? 0),
  nroComprobante: Number(raw.nroComprobante ?? raw.NroComprobante ?? 0),
  nroIdformularioDebito: Number(raw.nroIdformularioDebito ?? raw.NroIdformularioDebito ?? 0),
  fechaEmisionDebito: String(raw.fechaEmisionDebito ?? raw.FechaEmisionDebito ?? ''),
  nroMovCaja: Number(raw.nroMovCaja ?? raw.NroMovCaja ?? 0),
  montoCancelado: Number(raw.montoCancelado ?? raw.MontoCancelado ?? 0) || undefined,
  fechaAplicacion: (raw.fechaAplicacion ?? raw.FechaAplicacion) as string | undefined,
});

const firstPositiveNumber = (...vals: unknown[]): number => {
  for (const v of vals) {
    if (v == null || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

const mapAplicacionRecibo = (raw: Record<string, unknown>): AplicacionRecibo => ({
  nroIdformPagoCobro: firstPositiveNumber(
    raw.nroIdformPagoCobro,
    raw.NroIdformPagoCobro,
    raw.nroIdFormPagoCobro
  ),
  nroIdformulario: firstPositiveNumber(
    raw.nroIdformulario,
    raw.NroIdformulario,
    raw.nroIdFormulario,
    raw.NroIdFormulario,
    raw.nroFormulario,
    raw.NroFormulario
  ),
  nroTipoFormulario: String(raw.nroTipoFormulario ?? raw.NroTipoFormulario ?? ''),
  letra: String(raw.letra ?? raw.Letra ?? ''),
  nroSuc: Number(raw.nroSuc ?? raw.NroSuc ?? 0) || undefined,
  nroComprobante: Number(raw.nroComprobante ?? raw.NroComprobante ?? 0),
  fechaEmision: String(raw.fechaEmision ?? raw.FechaEmision ?? ''),
  fechaVencim: String(raw.fechaVencim ?? raw.FechaVencim ?? ''),
  importeTotal: Number(raw.importeTotal ?? raw.ImporteTotal ?? 0) || undefined,
  importeCanceladoAnt: Number(raw.importeCanceladoAnt ?? raw.ImporteCanceladoAnt ?? 0) || undefined,
  fecha: (raw.fecha ?? raw.Fecha) as string | undefined,
  nroMovCaja: Number(raw.nroMovCaja ?? raw.NroMovCaja ?? 0),
  montoCancelado: Number(raw.montoCancelado ?? raw.MontoCancelado ?? 0) || undefined,
});

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
};

const asRecordArray = (data: unknown): Record<string, unknown>[] => {
  if (typeof data === 'string') {
    try {
      return asRecordArray(JSON.parse(data) as unknown);
    } catch {
      return [];
    }
  }
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  const obj = asRecord(data);
  if (!obj) return [];
  const candidates = [
    obj.items,
    obj.Items,
    obj.data,
    obj.Data,
    obj.aplicaciones,
    obj.Aplicaciones,
    obj.documentosAplicar,
    obj.DocumentosAplicar,
    obj.documentosAplicados,
    obj.DocumentosAplicados,
    obj.result,
    obj.Result,
    obj.resultados,
    obj.Resultados,
  ];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate as Record<string, unknown>[];
  }
  for (const candidate of [obj.data, obj.Data, obj.result, obj.Result]) {
    const inner = asRecord(candidate);
    if (!inner) continue;
    const nested =
      inner.items ??
      inner.Items ??
      inner.aplicaciones ??
      inner.Aplicaciones ??
      inner.documentosAplicar ??
      inner.DocumentosAplicar;
    if (Array.isArray(nested)) return nested as Record<string, unknown>[];
  }
  return [];
};

const mapList = <T>(data: unknown, mapper: (raw: Record<string, unknown>) => T): T[] =>
  asRecordArray(data).map((item) => mapper((item ?? {}) as Record<string, unknown>));

const PAGE_SIZE_CREDITOS = 200;
const PAGE_SIZE_CUENTA_CORRIENTE = 50;
const MAX_PAGES_CREDITOS = 50;

const extractPagedItems = (
  data: unknown
): { items: Record<string, unknown>[]; totalCount?: number } => {
  const items = asRecordArray(data);
  if (Array.isArray(data)) return { items };
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    const totalRaw = obj.totalCount ?? obj.TotalCount;
    const totalCount = totalRaw == null ? undefined : Number(totalRaw);
    return { items, totalCount: Number.isFinite(totalCount) ? totalCount : undefined };
  }
  return { items };
};

const fetchAllPaged = async (
  url: string,
  extraParams: Record<string, string> = {},
  pageSize = PAGE_SIZE_CREDITOS
): Promise<Record<string, unknown>[]> => {
  const all: Record<string, unknown>[] = [];
  for (let page = 1; page <= MAX_PAGES_CREDITOS; page++) {
    const response = await api.get(url, {
      params: { ...extraParams, pageNumber: page, pageSize },
    });
    const { items, totalCount } = extractPagedItems(response.data);
    all.push(...items);
    if (items.length < pageSize) break;
    if (totalCount != null && all.length >= totalCount) break;
  }
  return all;
};

const tipoCreditoProveedorNormalizado = (nroTipo: string, texto = ''): '3' | '6' | undefined => {
  if (esNotaCreditoProveedor(nroTipo, texto)) return '3';
  if (esCreditoPagoProveedor(nroTipo, texto)) return '6';
  return undefined;
};

const parseLetraSucNro = (
  numero: string
): { letra?: string; nroSuc: number; nroComprobante?: number } => {
  const m = numero.trim().match(/([A-Za-z])-(\d+)-(\d+)/);
  if (!m) return { nroSuc: 0 };
  return {
    letra: m[1].toUpperCase(),
    nroSuc: Number(m[2]),
    nroComprobante: Number(m[3]),
  };
};

export interface FiltroImputacionesDebitoProveedor {
  numeroComprobanteDebito?: string;
  creditos?: CreditoConSaldoPendiente[];
}

/** Misma factura que muestra la OP: por id de formulario o por letra-suc-número. */
export const aplicacionApuntaADebitoProveedor = (
  app: AplicacionRecibo,
  nroFormularioDebito: number,
  numeroComprobanteDebito?: string
): boolean => {
  const debitoId = Number(nroFormularioDebito);
  const appId = Number(app.nroIdformulario);
  if (debitoId > 0 && appId === debitoId) return true;

  const parsed = parseLetraSucNro(numeroComprobanteDebito ?? '');
  const appNro = Number(app.nroComprobante);
  const appSuc = Number(app.nroSuc ?? 0);
  const appLetra = String(app.letra ?? '').trim().toUpperCase();

  if (parsed.nroComprobante && appNro === parsed.nroComprobante) {
    const letraOk = !parsed.letra || !appLetra || parsed.letra === appLetra;
    const sucOk = !parsed.nroSuc || !appSuc || appSuc === parsed.nroSuc;
    if (letraOk && sucOk) return true;
  }
  if (parsed.nroComprobante && appId === parsed.nroComprobante) return true;
  return false;
};

const roundMoney = (value: number): number => Math.round(value * 100) / 100;

const tieneSaldoPendiente = (value: number | undefined): boolean => roundMoney(value ?? 0) > 0;

const calcularMontoPendienteDevolucionDesdeCredito = (cred: CreditoConSaldoPendiente): number => {
  const importe = roundMoney(cred.importeTotal ?? 0);
  const aplicado = roundMoney(cred.totalAplicado ?? 0);
  if (importe > 0) {
    return roundMoney(Math.max(0, importe - aplicado));
  }
  return roundMoney(cred.saldoPendiente);
};

const importeTotalDevolucion = (debito: DebitoConSaldoPendiente): number =>
  roundMoney(debito.importeTotal ?? 0);

const mapCreditoDesdeReciboItem = (item: Record<string, unknown>): CreditoConSaldoPendiente => ({
  nroFormulario: Number(item.nroFormulario ?? item.NroFormulario ?? 0),
  nroTipoFormulario: String(item.nroTipoFormulario ?? item.NroTipoFormulario ?? '4').trim() || '4',
  letra: String(item.letra ?? item.Letra ?? ''),
  nroSuc: Number(item.nroSuc ?? item.NroSuc ?? 0),
  nroComprobante: Number(item.nroComprobante ?? item.NroComprobante ?? 0) || undefined,
  fechaEmision: (item.fechaEmision ?? item.FechaEmision) as string | undefined,
  importeTotal: Number(item.importeTotal ?? item.ImporteTotal ?? 0) || undefined,
  totalAplicado: 0,
  saldoPendiente: 0,
});

const mapCreditoDesdeOrdenPagoItem = (item: Record<string, unknown>): CreditoConSaldoPendiente => {
  const tipoRaw = String(
    item.nroTipoFormulario ?? item.NroTipoFormulario ?? item.tipoComprobante ?? '6'
  );
  return {
    nroFormulario: Number(item.nroFormulario ?? item.NroFormulario ?? 0),
    nroTipoFormulario: tipoCreditoProveedorNormalizado(tipoRaw, tipoRaw) ?? '6',
    letra: String(item.letra ?? item.Letra ?? ''),
    nroSuc: Number(item.nroSuc ?? item.NroSuc ?? 0),
    nroComprobante: Number(item.nroComprobante ?? item.NroComprobante ?? 0) || undefined,
    fechaEmision: (item.fechaEmision ?? item.FechaEmision) as string | undefined,
    importeTotal: Number(item.importeTotal ?? item.ImporteTotal ?? 0) || undefined,
    totalAplicado: 0,
    saldoPendiente: 0,
  };
};

const mapCreditoDesdeNotaCreditoCompraItem = (item: Record<string, unknown>): CreditoConSaldoPendiente => ({
  nroFormulario: Number(item.nroFormulario ?? item.NroFormulario ?? 0),
  nroTipoFormulario: '3',
  letra: String(item.letra ?? item.Letra ?? ''),
  nroSuc: Number(item.nroSuc ?? item.NroSuc ?? 0),
  nroComprobante: Number(item.nroComprobante ?? item.NroComprobante ?? 0) || undefined,
  fechaEmision: (item.fechaEmision ?? item.FechaEmision) as string | undefined,
  importeTotal: Number(item.importeTotal ?? item.ImporteTotal ?? 0) || undefined,
  totalAplicado: 0,
  saldoPendiente: 0,
});

const mapCreditoDesdeCuentaCorrienteItem = (
  item: Record<string, unknown>
): CreditoConSaldoPendiente | null => {
  const nroFormulario = Number(item.nroFormulario ?? item.NroFormulario ?? 0);
  if (nroFormulario <= 0) return null;
  const nroTipoRaw = String(item.nroTipoFormulario ?? item.NroTipoFormulario ?? '');
  const comprobante = String(item.comprobante ?? item.Comprobante ?? '');
  const numero = String(item.numero ?? item.Numero ?? '');
  const texto = `${comprobante} ${numero}`.trim();
  const tipo = tipoCreditoProveedorNormalizado(nroTipoRaw, texto);
  if (!tipo) return null;
  const parsed = parseLetraSucNro(numero);
  return {
    nroFormulario,
    nroTipoFormulario: tipo,
    letra: parsed.letra,
    nroSuc: parsed.nroSuc,
    nroComprobante: parsed.nroComprobante,
    fechaEmision: (item.fecha ?? item.Fecha) as string | undefined,
    importeTotal: Number(item.importe ?? item.Importe ?? 0) || undefined,
    totalAplicado: 0,
    saldoPendiente: 0,
  };
};

const addCreditoProveedor = (
  map: Map<number, CreditoConSaldoPendiente>,
  cred: CreditoConSaldoPendiente | null
) => {
  if (!cred || cred.nroFormulario <= 0 || map.has(cred.nroFormulario)) return;
  const tipo = tipoCreditoProveedorNormalizado(
    cred.nroTipoFormulario ?? '',
    cred.nroTipoFormulario ?? ''
  );
  map.set(cred.nroFormulario, tipo ? { ...cred, nroTipoFormulario: tipo } : cred);
};

/** Metadatos de recibos/NC del cliente (incluye los ya aplicados). */
const buildMapCreditosCliente = async (nroCliente: number): Promise<Map<number, CreditoConSaldoPendiente>> => {
  const map = new Map<number, CreditoConSaldoPendiente>();

  try {
    const response = await api.get(
      `/api/TesoreriaAplicaciones/clientes/${nroCliente}/creditos-con-saldo-pendiente`
    );
    for (const cred of mapList(response.data, mapCredito)) {
      if (cred.nroFormulario > 0) map.set(cred.nroFormulario, cred);
    }
  } catch {
    // Si falla, se intenta solo con el listado de recibos.
  }

  try {
    const response = await api.get('/api/tesoreria/recibos', {
      params: { nroCliente: String(nroCliente), pageNumber: 1, pageSize: 200 },
    });
    const data = response.data as Record<string, unknown> | unknown[];
    const items: Record<string, unknown>[] = Array.isArray(data)
      ? (data as Record<string, unknown>[])
      : ((data?.items ?? data?.Items ?? []) as Record<string, unknown>[]);
    for (const item of items) {
      const cred = mapCreditoDesdeReciboItem(item);
      if (cred.nroFormulario > 0 && !map.has(cred.nroFormulario)) {
        map.set(cred.nroFormulario, cred);
      }
    }
  } catch {
    // Si falla el listado de recibos, se usa solo créditos con saldo pendiente.
  }

  return map;
};

/** Metadatos de órdenes de pago / NC del proveedor (incluye las ya aplicadas). */
const buildMapCreditosProveedor = async (nroProveedor: number): Promise<Map<number, CreditoConSaldoPendiente>> => {
  const map = new Map<number, CreditoConSaldoPendiente>();

  try {
    const creditos = await tesoreriaAplicacionesService.getCreditosProveedorConSaldoPendiente(nroProveedor);
    for (const cred of creditos) addCreditoProveedor(map, cred);
  } catch {
    // Si falla, se intenta solo con el listado de órdenes de pago.
  }

  try {
    const items = await fetchAllPaged('/api/Tesoreria/ordendepago', {
      nroCliente: String(nroProveedor),
      NroCliente: String(nroProveedor),
    });
    for (const item of items) {
      const nroProvItem = Number(
        item.nroProveedor ?? item.NroProveedor ?? item.nroCliente ?? item.NroCliente ?? 0
      );
      if (nroProvItem > 0 && nroProvItem !== Number(nroProveedor)) continue;
      addCreditoProveedor(map, mapCreditoDesdeOrdenPagoItem(item));
    }
  } catch {
    // Si falla el listado de OP, se usa solo créditos con saldo pendiente.
  }

  try {
    const items = await fetchAllPaged('/api/NotasCreditoCompra', {
      nroProveedor: String(nroProveedor),
    });
    for (const item of items) addCreditoProveedor(map, mapCreditoDesdeNotaCreditoCompraItem(item));
  } catch {
    // Si falla el listado de NC, se usa solo OP y créditos con saldo pendiente.
  }

  try {
    const items = await fetchAllPaged(
      `/api/CuentaCorrienteProveedores/proveedor/${nroProveedor}`,
      {},
      PAGE_SIZE_CUENTA_CORRIENTE
    );
    for (const item of items) addCreditoProveedor(map, mapCreditoDesdeCuentaCorrienteItem(item));
  } catch {
    // Si falla CC, se usan OP/NC y créditos con saldo pendiente.
  }

  return map;
};

/** OP + NC del proveedor con saldo (API) y metadatos de listados (incluye ya aplicados). */
export const getCreditosProveedorParaAplicaciones = async (
  nroProveedor: number
): Promise<CreditoConSaldoPendiente[]> => {
  const map = await buildMapCreditosProveedor(nroProveedor);
  return Array.from(map.values()).filter((c) => c.nroFormulario > 0);
};

export const enriquecerCreditoProveedorConSaldo = async (
  cred: CreditoConSaldoPendiente
): Promise<CreditoConSaldoPendiente> => {
  if (roundMoney(cred.saldoPendiente ?? 0) > 0) return cred;
  const importe = roundMoney(cred.importeTotal ?? 0);
  if (importe <= 0) return cred;

  const apps = await tesoreriaAplicacionesService.getAplicacionesCreditoProveedor(
    cred.nroFormulario,
    cred.nroTipoFormulario
  );
  const totalAplicado = roundMoney(apps.reduce((sum, app) => sum + (app.montoCancelado ?? 0), 0));
  const saldoPendiente = roundMoney(Math.max(0, importe - totalAplicado));
  return { ...cred, totalAplicado, saldoPendiente };
};

/** El API a veces devuelve el débito (devolución) en lugar del crédito (recibo/NC). */
const corregirImputacionCredito = (
  app: AplicacionCreditoEnDebito,
  meta: CreditoConSaldoPendiente | undefined
): AplicacionCreditoEnDebito => {
  if (!meta || !esDevolucionCobranzaCliente(app.nroTipoFormularioCredito, '')) return app;
  return {
    ...app,
    nroTipoFormularioCredito: meta.nroTipoFormulario ?? '4',
    letra: meta.letra ?? app.letra,
    nroSuc: meta.nroSuc,
    nroComprobante: meta.nroComprobante ?? app.nroComprobante,
  };
};

/** Devoluciones (tipo 60) pendientes que el API aún expone como crédito → destinos de imputación (débito). */
export const devolucionCreditoADebito = (cred: CreditoConSaldoPendiente): DebitoConSaldoPendiente | null => {
  if (!esDevolucionCobranzaCliente(cred.nroTipoFormulario ?? '', '')) return null;
  if (cred.nroFormulario <= 0) return null;

  const montoPendiente = calcularMontoPendienteDevolucionDesdeCredito(cred);
  if (!tieneSaldoPendiente(montoPendiente)) return null;

  const importeTotal = roundMoney(cred.importeTotal ?? 0);
  const importeCancelado =
    importeTotal > 0
      ? roundMoney(Math.max(cred.totalAplicado ?? 0, importeTotal - montoPendiente))
      : roundMoney(cred.totalAplicado ?? 0);

  return {
    nroFormulario: cred.nroFormulario,
    nroTipoFormulario: cred.nroTipoFormulario ?? '60',
    letra: cred.letra,
    nroSuc: cred.nroSuc,
    nroComprobante: cred.nroComprobante,
    fechaEmision: cred.fechaEmision,
    importeTotal: cred.importeTotal,
    importeCancelado,
    montoPendiente,
  };
};

/** Une débitos del API con devoluciones pendientes expuestas como créditos. */
export const mergeDebitosClienteConDevoluciones = (
  debitos: DebitoConSaldoPendiente[],
  creditos: CreditoConSaldoPendiente[]
): DebitoConSaldoPendiente[] => {
  const merged = debitos.filter((d) => tieneSaldoPendiente(d.montoPendiente));
  for (const cred of creditos) {
    const dev = devolucionCreditoADebito(cred);
    if (!dev) continue;
    if (!merged.some((d) => d.nroFormulario === dev.nroFormulario)) {
      merged.push(dev);
    }
  }
  return merged.filter((d) => tieneSaldoPendiente(d.montoPendiente));
};

const tesoreriaAplicacionesService = {
  getCreditosConSaldoPendiente: async (nroCliente: number): Promise<CreditoConSaldoPendiente[]> => {
    const response = await api.get(
      `/api/TesoreriaAplicaciones/clientes/${nroCliente}/creditos-con-saldo-pendiente`
    );
    return mapList(response.data, mapCredito);
  },

  getDebitosConSaldoPendiente: async (nroCliente: number): Promise<DebitoConSaldoPendiente[]> => {
    const response = await api.get(
      `/api/TesoreriaAplicaciones/clientes/${nroCliente}/debitos-con-saldo-pendiente`
    );
    return mapList(response.data, mapDebito);
  },

  getAplicacionesRecibo: async (reciboId: number): Promise<AplicacionRecibo[]> => {
    const response = await api.get<AplicacionRecibo[]>(
      `/api/TesoreriaAplicaciones/recibos/${reciboId}/aplicaciones`
    );
    return response.data ?? [];
  },

  getAplicacionesCreditoEnDebitoCliente: async (
    nroCliente: number,
    nroFormularioDebito: number
  ): Promise<AplicacionCreditoEnDebito[]> => {
    const response = await api.get(
      `/api/TesoreriaAplicaciones/clientes/${nroCliente}/debitos/${nroFormularioDebito}/aplicaciones-credito`
    );
    return mapList(response.data, mapAplicacionCreditoEnDebito);
  },

  /**
   * Imputaciones recibidas en un débito (FC, ND, devolución).
   * Para devoluciones, si el endpoint de débitos aún no las reconoce, busca en aplicaciones de recibos/NC.
   */
  getImputacionesCreditoEnDebitoCliente: async (
    nroCliente: number,
    nroFormularioDebito: number,
    nroTipoFormulario?: string
  ): Promise<AplicacionCreditoEnDebito[]> => {
    const esDevolucion = esDevolucionCobranzaCliente(nroTipoFormulario ?? '', '');

    try {
      const apps = await tesoreriaAplicacionesService.getAplicacionesCreditoEnDebitoCliente(
        nroCliente,
        nroFormularioDebito
      );

      if (!esDevolucion) return apps;

      const respuestaIncorrecta =
        apps.length === 0 ||
        apps.some((a) => esDevolucionCobranzaCliente(a.nroTipoFormularioCredito, ''));

      if (respuestaIncorrecta) {
        const fallback = await tesoreriaAplicacionesService.buscarImputacionesCreditoEnDevolucion(
          nroCliente,
          nroFormularioDebito
        );
        if (fallback.length > 0) return fallback;
      }

      const meta = await buildMapCreditosCliente(nroCliente);
      return apps.map((a) => corregirImputacionCredito(a, meta.get(a.nroIdformPagoCobro)));
    } catch (error) {
      if (!esDevolucion) throw error;
      return tesoreriaAplicacionesService.buscarImputacionesCreditoEnDevolucion(
        nroCliente,
        nroFormularioDebito
      );
    }
  },

  buscarImputacionesCreditoEnDevolucion: async (
    nroCliente: number,
    nroFormularioDevolucion: number
  ): Promise<AplicacionCreditoEnDebito[]> => {
    const metaCreditos = await buildMapCreditosCliente(nroCliente);
    const creditoIds = new Set<number>();

    for (const [id, cred] of Array.from(metaCreditos.entries())) {
      if (esCreditoCobranzaCliente(cred.nroTipoFormulario ?? '', '')) {
        creditoIds.add(id);
      }
    }

    const result: AplicacionCreditoEnDebito[] = [];
    const claves = new Set<string>();

    for (const creditoId of Array.from(creditoIds)) {
      const meta = metaCreditos.get(creditoId);
      const apps = await tesoreriaAplicacionesService.getAplicacionesRecibo(creditoId);
      for (const app of apps) {
        if (app.nroIdformulario !== nroFormularioDevolucion) continue;
        const clave = `${creditoId}|${app.nroIdformulario}|${app.nroMovCaja}|${app.fechaEmision}`;
        if (claves.has(clave)) continue;
        claves.add(clave);
        result.push({
          nroIdformPagoCobro: creditoId,
          nroTipoFormularioCredito: meta?.nroTipoFormulario ?? '4',
          letra: meta?.letra ?? app.letra,
          nroSuc: meta?.nroSuc ?? app.nroSuc ?? 0,
          nroComprobante: meta?.nroComprobante ?? app.nroComprobante,
          nroIdformularioDebito: nroFormularioDevolucion,
          fechaEmisionDebito: app.fechaEmision,
          nroMovCaja: app.nroMovCaja,
          montoCancelado: app.montoCancelado,
          fechaAplicacion: app.fecha,
        });
      }
    }

    return result;
  },

  aplicarSaldoRecibo: async (
    reciboId: number,
    documentosAplicar: DocumentoAplicar[]
  ): Promise<void> => {
    await api.post(`/api/TesoreriaAplicaciones/recibos/${reciboId}/aplicar-saldo`, {
      documentosAplicar,
    });
  },

  desaplicarRecibo: async (
    reciboId: number,
    request: DesaplicarRequest
  ): Promise<void> => {
    await api.delete(`/api/TesoreriaAplicaciones/recibos/${reciboId}/aplicaciones`, {
      data: request,
    });
  },

  /** Mismo contrato que cliente; `nroProveedor` es Formulario.NroCliente con TipoCliente P */
  getCreditosProveedorConSaldoPendiente: async (nroProveedor: number): Promise<CreditoConSaldoPendiente[]> => {
    const response = await api.get(
      `/api/TesoreriaAplicaciones/proveedores/${nroProveedor}/creditos-con-saldo-pendiente`
    );
    return mapList(response.data, mapCredito);
  },

  getDebitosProveedorConSaldoPendiente: async (nroProveedor: number): Promise<DebitoConSaldoPendiente[]> => {
    const response = await api.get(
      `/api/TesoreriaAplicaciones/proveedores/${nroProveedor}/debitos-con-saldo-pendiente`
    );
    return mapList(response.data, mapDebito);
  },

  getAplicacionesPagoProveedor: async (nroFormularioCredito: number): Promise<AplicacionRecibo[]> => {
    const response = await api.get(
      `/api/TesoreriaAplicaciones/proveedores/pagos/${nroFormularioCredito}/aplicaciones`
    );
    return mapList(response.data, mapAplicacionRecibo);
  },

  /** Aplicaciones de OP o NC de compra (misma tabla de imputaciones). */
  getAplicacionesCreditoProveedor: async (
    nroFormularioCredito: number,
    nroTipoFormulario?: string
  ): Promise<AplicacionRecibo[]> => {
    const tipo = (nroTipoFormulario ?? '').trim();
    const esNc = tipo === '3' || esNotaCreditoProveedor(tipo, tipo);
    const rutaPagos = `/api/TesoreriaAplicaciones/proveedores/pagos/${nroFormularioCredito}/aplicaciones`;
    const rutaNc = `/api/TesoreriaAplicaciones/proveedores/notas-credito/${nroFormularioCredito}/aplicaciones`;
    const rutas = esNc
      ? [rutaNc, rutaPagos]
      : tipo
        ? [rutaPagos]
        : [rutaPagos, rutaNc];

    for (let i = 0; i < rutas.length; i++) {
      try {
        const response = await api.get(rutas[i]);
        const data = mapList(response.data, mapAplicacionRecibo);
        if (data.length > 0 || i === rutas.length - 1) return data;
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404 && i < rutas.length - 1) continue;
        if (status === 404) return [];
        throw error;
      }
    }
    return [];
  },

  getAplicacionesCreditoEnDebitoProveedor: async (
    nroProveedor: number,
    nroFormularioDebito: number
  ): Promise<AplicacionCreditoEnDebito[]> => {
    const response = await api.get(
      `/api/TesoreriaAplicaciones/proveedores/${nroProveedor}/debitos/${nroFormularioDebito}/aplicaciones-credito`
    );
    return mapList(response.data, mapAplicacionCreditoEnDebito);
  },

  /**
   * Imputaciones recibidas en un débito de proveedor (FC, ND).
   * Si el endpoint dedicado no existe o no devuelve datos, busca en aplicaciones de OP/NC.
   */
  getImputacionesCreditoEnDebitoProveedor: async (
    nroProveedor: number,
    nroFormularioDebito: number,
    filtro?: FiltroImputacionesDebitoProveedor
  ): Promise<AplicacionCreditoEnDebito[]> => {
    try {
      const apps = await tesoreriaAplicacionesService.getAplicacionesCreditoEnDebitoProveedor(
        nroProveedor,
        nroFormularioDebito
      );
      if (apps.length > 0) return apps;
      return tesoreriaAplicacionesService.buscarImputacionesCreditoEnDebitoProveedor(
        nroProveedor,
        nroFormularioDebito,
        filtro
      );
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        return tesoreriaAplicacionesService.buscarImputacionesCreditoEnDebitoProveedor(
          nroProveedor,
          nroFormularioDebito,
          filtro
        );
      }
      throw error;
    }
  },

  buscarImputacionesCreditoEnDebitoProveedor: async (
    nroProveedor: number,
    nroFormularioDebito: number,
    filtro?: FiltroImputacionesDebitoProveedor
  ): Promise<AplicacionCreditoEnDebito[]> => {
    const metaCreditos = await buildMapCreditosProveedor(nroProveedor);
    for (const cred of filtro?.creditos ?? []) {
      if (!cred || cred.nroFormulario <= 0) continue;
      const prev = metaCreditos.get(cred.nroFormulario);
      metaCreditos.set(
        cred.nroFormulario,
        prev
          ? {
              ...prev,
              ...cred,
              nroTipoFormulario: cred.nroTipoFormulario || prev.nroTipoFormulario,
              letra: cred.letra || prev.letra,
              nroSuc: cred.nroSuc || prev.nroSuc,
              nroComprobante: cred.nroComprobante || prev.nroComprobante,
            }
          : cred
      );
    }
    const creditoIds = new Set<number>();

    for (const [id, cred] of Array.from(metaCreditos.entries())) {
      if (esCreditoPagoProveedor(cred.nroTipoFormulario ?? '', cred.nroTipoFormulario ?? '')) {
        creditoIds.add(id);
      }
    }

    const result: AplicacionCreditoEnDebito[] = [];
    const claves = new Set<string>();
    const debitoId = Number(nroFormularioDebito);
    const numeroDebito = filtro?.numeroComprobanteDebito;

    for (const creditoId of Array.from(creditoIds)) {
      const meta = metaCreditos.get(creditoId);
      let apps: AplicacionRecibo[] = [];
      try {
        apps = await tesoreriaAplicacionesService.getAplicacionesCreditoProveedor(
          creditoId,
          meta?.nroTipoFormulario
        );
      } catch {
        apps = [];
      }
      for (const app of apps) {
        if (!aplicacionApuntaADebitoProveedor(app, debitoId, numeroDebito)) continue;
        const clave = `${creditoId}|${app.nroIdformulario}|${app.nroMovCaja}|${app.fechaEmision}`;
        if (claves.has(clave)) continue;
        claves.add(clave);
        result.push({
          nroIdformPagoCobro: creditoId,
          nroTipoFormularioCredito: meta?.nroTipoFormulario ?? '6',
          letra: meta?.letra ?? app.letra,
          nroSuc: meta?.nroSuc ?? app.nroSuc ?? 0,
          nroComprobante: meta?.nroComprobante ?? app.nroComprobante,
          nroIdformularioDebito: debitoId,
          fechaEmisionDebito: app.fechaEmision,
          nroMovCaja: app.nroMovCaja,
          montoCancelado: app.montoCancelado,
          fechaAplicacion: app.fecha,
        });
      }
    }

    return result;
  },

  aplicarSaldoPagoProveedor: async (
    nroFormularioCredito: number,
    documentosAplicar: DocumentoAplicar[],
    nroTipoFormulario?: string
  ): Promise<void> => {
    const tipo = (nroTipoFormulario ?? '').trim();
    const rutas = [
      `/api/TesoreriaAplicaciones/proveedores/pagos/${nroFormularioCredito}/aplicar-saldo`,
    ];
    if (tipo === '3' || esNotaCreditoProveedor(tipo, '')) {
      rutas.push(
        `/api/TesoreriaAplicaciones/proveedores/notas-credito/${nroFormularioCredito}/aplicar-saldo`
      );
    }

    let lastError: unknown;
    for (let i = 0; i < rutas.length; i++) {
      try {
        await api.post(rutas[i], { documentosAplicar });
        return;
      } catch (error) {
        lastError = error;
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404 && i < rutas.length - 1) continue;
        throw error;
      }
    }
    throw lastError;
  },

  desaplicarPagoProveedor: async (
    nroFormularioCredito: number,
    request: DesaplicarRequest,
    nroTipoFormulario?: string
  ): Promise<void> => {
    const tipo = (nroTipoFormulario ?? '').trim();
    const rutas = [
      `/api/TesoreriaAplicaciones/proveedores/pagos/${nroFormularioCredito}/aplicaciones`,
    ];
    if (tipo === '3' || esNotaCreditoProveedor(tipo, '')) {
      rutas.push(
        `/api/TesoreriaAplicaciones/proveedores/notas-credito/${nroFormularioCredito}/aplicaciones`
      );
    }

    let lastError: unknown;
    for (let i = 0; i < rutas.length; i++) {
      try {
        await api.delete(rutas[i], { data: request });
        return;
      } catch (error) {
        lastError = error;
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 404 && i < rutas.length - 1) continue;
        throw error;
      }
    }
    throw lastError;
  },
};

/** Recalcula saldo pendiente de devoluciones según imputaciones reales (recibos/NC). */
const ajustarMontosPendientesDevoluciones = async (
  nroCliente: number,
  debitos: DebitoConSaldoPendiente[]
): Promise<DebitoConSaldoPendiente[]> => {
  const devoluciones = debitos.filter((d) => esDevolucionCobranzaCliente(d.nroTipoFormulario ?? '', ''));
  if (devoluciones.length === 0) return debitos;

  const ajustes = await Promise.all(
    devoluciones.map(async (devolucion) => {
      const imputaciones = await tesoreriaAplicacionesService.getImputacionesCreditoEnDebitoCliente(
        nroCliente,
        devolucion.nroFormulario,
        '60'
      );
      const totalImputado = roundMoney(
        imputaciones.reduce((sum, app) => sum + (app.montoCancelado ?? 0), 0)
      );
      const importe = importeTotalDevolucion(devolucion);
      const importeBase =
        importe > 0 ? importe : roundMoney(devolucion.montoPendiente + (devolucion.importeCancelado ?? 0));
      const montoPendiente = roundMoney(Math.max(0, importeBase - totalImputado));

      return {
        nroFormulario: devolucion.nroFormulario,
        montoPendiente,
        importeCancelado: totalImputado,
      };
    })
  );

  const ajustePorId = new Map(ajustes.map((a) => [a.nroFormulario, a]));
  return debitos
    .map((debito) => {
      const ajuste = ajustePorId.get(debito.nroFormulario);
      if (!ajuste || !esDevolucionCobranzaCliente(debito.nroTipoFormulario ?? '', '')) return debito;
      return {
        ...debito,
        montoPendiente: ajuste.montoPendiente,
        importeCancelado: ajuste.importeCancelado,
      };
    })
    .filter((d) => tieneSaldoPendiente(d.montoPendiente));
};

/** Débitos disponibles para aplicar saldo desde recibo/NC (incluye devoluciones con saldo real). */
export const getDebitosParaAplicarCliente = async (
  nroCliente: number,
  debitos: DebitoConSaldoPendiente[],
  creditos: CreditoConSaldoPendiente[]
): Promise<DebitoConSaldoPendiente[]> => {
  const merged = mergeDebitosClienteConDevoluciones(debitos, creditos);
  return ajustarMontosPendientesDevoluciones(nroCliente, merged);
};

export default tesoreriaAplicacionesService;
